import { withLanguagePrefix } from '@/lib/locale';
import { BASE_URL } from '@/seo/meta';

export interface ServiceSEOProps {
  title: string;
  description: string;
  keywords: string[];
  canonical: string;
}

export const generateServiceSEO = (props: ServiceSEOProps) => {
  const { title, description, keywords, canonical } = props;

  return {
    title,
    meta: [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords.join(', ') },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: canonical },
      { property: 'og:image', content: `${typeof window !== 'undefined' ? window.location.origin : 'https://pixelpro.solutions'}/og-image.jpg` },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: `${typeof window !== 'undefined' ? window.location.origin : 'https://pixelpro.solutions'}/og-image.jpg` },
    ],
    canonical,
  };
};

export const generateBreadcrumbSchema = (
  serviceName: string,
  serviceSlug: string,
  language: 'el' | 'en' = 'el',
) => {
  const homeLabel = language === 'en' ? 'Home' : 'Αρχική';
  const servicesLabel = language === 'en' ? 'Services' : 'Υπηρεσίες';
  const homePath = withLanguagePrefix('/', language);
  const servicesPath = withLanguagePrefix('/services', language);
  const servicePath = withLanguagePrefix(`/services/${serviceSlug}`, language);

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: homeLabel,
        item: `${BASE_URL}${homePath}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: servicesLabel,
        item: `${BASE_URL}${servicesPath}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: serviceName,
        item: `${BASE_URL}${servicePath}`,
      },
    ],
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

// Article schema for blog posts

export const generateArticleSchema = (article: {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: { name: string; url: string };
  publisher?: { name: string; logo: string };
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.description,
  image: article.image,
  datePublished: article.datePublished,
  dateModified: article.dateModified,
  author: [{
    '@type': 'Person',
    name: article.author.name,
    url: article.author.url
  }],
  publisher: article.publisher || {
    '@type': 'Organization',
    name: 'PixelPro Solutions',
    logo: {
      '@type': 'ImageObject',
      url: 'https://pixelpro.solutions/logo.svg'
    }
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': 'https://pixelpro.solutions/articles'
  },
  articleSection: 'Blog'
});

// Person schema for team members
export const generatePersonSchema = (person: {
  name: string;
  jobTitle: string;
  image: string;
  sameAs: string[];
  description?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: person.name,
  jobTitle: person.jobTitle,
  image: person.image,
  sameAs: person.sameAs,
  description: person.description,
  worksFor: {
    '@type': 'Organization',
    name: 'PixelPro Solutions',
    url: 'https://pixelpro.solutions'
  }
});

// Review/AggregateRating schema for portfolio
export const generateReviewSchema = (review: {
  itemReviewed: string;
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
  reviewBody?: string;
  author?: { name: string; url: string };
}) => ({
  '@context': 'https://schema.org',
  '@type': review.reviewBody ? 'Review' : 'Product',
  name: review.itemReviewed,
  reviewRating: {
    '@type': 'Rating',
    ratingValue: review.ratingValue,
    bestRating: review.bestRating || 5
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: review.ratingValue,
    reviewCount: review.reviewCount,
    bestRating: review.bestRating || 5
  },
  ...(review.reviewBody && {
    reviewBody: review.reviewBody,
    author: review.author
  })
});

// WebPage schema for each page type
export const generateWebPageSchema = (page: {
  path: string;
  name: string;
  description: string;
  keywords?: string[];
}) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `https://pixelpro.solutions${page.path}`,
  name: page.name,
  description: page.description,
  url: `https://pixelpro.solutions${page.path}`,
  keywords: page.keywords?.join(', '),
  mainEntity: {
    '@type': 'WebPageElement',
    name: page.name
  }
});

// LocalBusiness schema for local SEO
export const generateLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': 'https://pixelpro.solutions/#organization',
  name: 'PixelPro Solutions',
  image: 'https://pixelpro.solutions/logo.svg',
  logo: 'https://pixelpro.solutions/logo.svg',
  description: 'Ολοκληρωμένες ψηφιακές λύσεις για επιχειρήσεις. Web development, e-commerce, branding και digital marketing.',
  url: 'https://pixelpro.solutions',
  telephone: '+30-210-1234567',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Athens',
    addressLocality: 'Athens',
    addressRegion: 'Attica',
    postalCode: '12345',
    addressCountry: 'GR'
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 37.9838,
    longitude: 23.7275
  },
  openingHoursSpecification: [{
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    opens: '09:00',
    closes: '18:00'
  }],
  areaServed: {
    '@type': 'Country',
    name: 'Greece'
  },
  sameAs: [
    'https://www.facebook.com/pixelpro.solutions',
    'https://www.linkedin.com/company/pixelpro-solutions',
    'https://www.instagram.com/pixelpro.solutions'
  ]
});

// CreativeWork schema for portfolio items
export const generateCreativeWorkSchema = (work: {
  name: string;
  description: string;
  image: string;
  url: string;
  dateCreated: string;
  creator?: { name: string; url: string };
}) => ({
  '@context': 'https://schema.org',
  '@type': 'CreativeWork',
  name: work.name,
  description: work.description,
  image: work.image,
  url: work.url,
  dateCreated: work.dateCreated,
  creator: work.creator || {
    '@type': 'Organization',
    name: 'PixelPro Solutions',
    url: 'https://pixelpro.solutions'
  },
  about: {
    '@type': 'Thing',
    name: 'Web Development & Digital Marketing'
  }
});

// Breadcrumb schema already exists
// FAQ schema already exists
// Organization schema already exists
// Website schema already exists
// Service schema already exists
export const generateOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'PixelPro Solutions',
    'url': 'https://pixelpro.solutions',
    'logo': 'https://pixelpro.solutions/logo.svg',
    'description': 'Ολοκληρωμένες ψηφιακές λύσεις για επιχειρήσεις που αναπτύσσονται. Web development, e-commerce, branding και digital marketing.',
    'foundingDate': '2020',
    'contactPoint': {
      '@type': 'ContactPoint',
      'telephone': '+30-697-3517-695',
      'contactType': 'customer service',
      'availableLanguage': ['Greek', 'English']
    },
    'sameAs': [
      'https://www.facebook.com/pixelpro.solutions',
      'https://www.linkedin.com/company/pixelpro-solutions',
      'https://www.instagram.com/pixelpro.solutions',
      'https://www.youtube.com/@PixelproSolutions'
    ]
  };
};

// Website schema (site-wide)
export const generateWebsiteSchema = (language: 'el' | 'en' = 'el') => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'PixelPro Solutions',
    'url': `${BASE_URL}${withLanguagePrefix('/', language)}`,
    'description':
      language === 'en'
        ? 'End-to-end digital solutions for your business'
        : 'Ολοκληρωμένες ψηφιακές λύσεις για την επιχείρησή σας',
    'inLanguage': language,
    'publisher': {
      '@type': 'Organization',
      'name': 'PixelPro Solutions',
      'url': BASE_URL
    },
    'hasPart': [
      {
        '@type': 'SiteNavigationElement',
        name: language === 'en' ? 'Services' : 'Υπηρεσίες',
        url: `${BASE_URL}${withLanguagePrefix('/services', language)}`
      },
      {
        '@type': 'SiteNavigationElement',
        name: language === 'en' ? 'About' : 'Σχετικά',
        url: `${BASE_URL}${withLanguagePrefix('/about', language)}`
      },
      {
        '@type': 'SiteNavigationElement',
        name: language === 'en' ? 'Articles' : 'Άρθρα',
        url: `${BASE_URL}${withLanguagePrefix('/articles', language)}`
      },
      {
        '@type': 'SiteNavigationElement',
        name: language === 'en' ? 'Projects' : 'Έργα',
        url: `${BASE_URL}${withLanguagePrefix('/projects', language)}`
      },
      {
        '@type': 'SiteNavigationElement',
        name: language === 'en' ? 'Free Consultation' : 'Δωρεάν Συμβουλευτική',
        url: `${BASE_URL}${withLanguagePrefix('/consultation', language)}`
      }
    ]
  };
};

// Enhanced Service schema
export const generateServiceSchema = (name: string, description: string, url: string, serviceType?: string) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': name,
    'description': description,
    'provider': {
      '@type': 'Organization',
      'name': 'PixelPro Solutions',
      'url': 'https://pixelpro.solutions'
    },
    'areaServed': {
      '@type': 'Country',
      'name': 'Greece'
    },
    'serviceType': serviceType || 'Digital Services',
    'url': url
  };
};

// WebSite search schema for better search understanding
export const generateWebSiteSearchSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'PixelPro Solutions',
    'url': 'https://pixelpro.solutions',
    'description': 'Ολοκληρωμένες ψηφιακές λύσεις για επιχειρήσεις που αναπτύσσονται',
    'inLanguage': 'el',
    'copyrightHolder': {
      '@type': 'Organization',
      'name': 'PixelPro Solutions'
    },
    'hasPart': [
      {
        '@type': 'SiteNavigationElement',
        name: 'Υπηρεσίες',
        url: 'https://pixelpro.solutions/services'
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'Σχετικά',
        url: 'https://pixelpro.solutions/about'
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'Άρθρα',
        url: 'https://pixelpro.solutions/articles'
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'Έργα',
        url: 'https://pixelpro.solutions/projects'
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'Δωρεάν Συμβουλευτική',
        url: 'https://pixelpro.solutions/consultation'
      }
    ]
  };
};

// Professional Service schema for local business
export const generateProfessionalServiceSchema = (serviceName: string, serviceSlug: string) => {
  const baseUrl = 'https://pixelpro.solutions';
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    'name': serviceName,
    'serviceType': 'Digital Agency Services',
    'areaServed': {
      '@type': 'Country',
      'name': 'GR'
    },
    'provider': {
      '@type': 'Organization',
      'name': 'PixelPro Solutions',
      'url': baseUrl,
      'address': {
        '@type': 'PostalAddress',
        'addressCountry': 'GR',
        'addressLocality': 'Athens'
      }
    },
    'url': `${baseUrl}/services/${serviceSlug}/`,
    'description': `Professional ${serviceName.toLowerCase()} services by PixelPro Solutions`
  };
};
